import React from "react";
import {
  defineMessages,
  FormattedMessage as T,
  useIntl,
  IntlShape,
} from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import RestoreIcon from "@material-ui/icons/Restore";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import CustomAutocomplete from "../ControlForm/CustomAutocomplete";
import useCityConfig from "../../../hooks/useCityConfig";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import {
  Zone,
  Sector,
  Ticket,
  LapiControl,
  isTicketInGracePeriod,
} from "@egis-smartcity/parking";
import * as yup from "yup";
import {
  init,
  initControleFeature,
  addressUpdated,
  history,
  updateSecteur,
  newControleFeature,
} from "../../../lib/carto/main";
import { noop } from "../../../utils";
import globalState from "../../globalState";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../../FullscreenNotification";

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    height: "100%",
  },
  /* Styles applied to the location control header. */
  header: {
    height: 48,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 8,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
  },
  /* Styles applied to the location control body. */
  body: {
    width: "100%",
    height: "calc(100% - 48px)",
    display: "flex",
    flexDirection: "column",
  },
  resetButton: {
    backgroundColor: "white",
    color: theme.palette.primary.main,
    borderRadius: 4,
    height: 32,
    width: 32,
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
      transition: "all 150ms ease-in-out",
    },
  },
  /* Styles applied to the location control form. */
  form: {
    padding: 8,
    overflow: "auto",
  },
}));

const msg = defineMessages({
  title: {
    id: "locationControl.title",
    description: "Title of the LocationControl component.",
    defaultMessage: "Location",
  },
  restoreButtonLabel: {
    id: "locationControl.restoreButton.label",
    description: "Message displayed to labeled the restore button.",
    defaultMessage: "Reset",
  },
  zoneLabel: {
    id: "locationControl.zone.label",
    description: "Message displayed to labeled the zone field.",
    defaultMessage: "Zone",
  },
  zoneRequired: {
    id: "locationControl.zone.required",
    description: "Message displayed when the zone field is empty.",
    defaultMessage: "The zone is required",
  },
  sectorLabel: {
    id: "locationControl.sector.label",
    description: "Message displayed to labeled the sector field.",
    defaultMessage: "Sector",
  },
  sectorRequired: {
    id: "locationControl.sector.required",
    description: "Message displayed when the sector field is empty.",
    defaultMessage: "The sector is required",
  },
  streetNumberLabel: {
    id: "locationControl.streetNumber.label",
    description: "Message displayed to labeled the streetNumber field.",
    defaultMessage: "Street Number",
  },
  streetNumberRequired: {
    id: "locationControl.streetNumber.required",
    description: "Message displayed when the streetNumber field is empty.",
    defaultMessage: "The Street Number is required",
  },
  addressLabel: {
    id: "locationControl.address.label",
    description: "Message displayed to labeled the address field.",
    defaultMessage: "Address",
  },
  addressRequired: {
    id: "locationControl.address.required",
    description: "Message displayed when the address field is empty.",
    defaultMessage: "The address is required",
  },
  postalCodeLabel: {
    id: "locationControl.postalCode.label",
    description: "Message displayed to labeled the postalCode field.",
    defaultMessage: "Postal code",
  },
  postalCodeRequired: {
    id: "locationControl.postalCode.required",
    description: "Message displayed when the postalCode field is empty.",
    defaultMessage: "The postal code is required",
  },
  postalCodeNotAllowed: {
    id: "locationControl.postalCode.notAllowed",
    description: "Message displayed when the postalCode field is invalid.",
    defaultMessage: "The postal code is not allowed",
  },
  cityLabel: {
    id: "locationControl.city.label",
    description: "Message displayed to labeled the city field.",
    defaultMessage: "City",
  },
  cityRequired: {
    id: "locationControl.city.required",
    description: "Message displayed when the city field is empty.",
    defaultMessage: "The city is required",
  },
  cityNotAllowed: {
    id: "locationControl.city.notAllowed",
    description: "Message displayed when the city field is invalid.",
    defaultMessage: "The city is not allowed",
  },
  gracePeriodNotification: {
    id: "locationControl.gracePeriodNotification",
    description:
      "Message displayed as a notification when there is a ticket in grace period.",
    defaultMessage: "The control has a ticket in grace period",
  },
});

export function getLocationControlFormSchema(
  y: typeof yup,
  t: IntlShape["formatMessage"],
  dedicatedStreetNumber: boolean | undefined,
  zipcodesAllowed: string[],
  citiesAllowed: string[]
) {
  return y.object().shape({
    zone: y
      .object({
        label: y.string().required(t(msg.zoneRequired)),
        value: y.string().required(t(msg.zoneRequired)),
      })
      .required(t(msg.zoneRequired)),
    sector: y
      .object({
        label: y.string().required(t(msg.sectorRequired)),
        value: y.string().required(t(msg.sectorRequired)),
      })
      .required(t(msg.sectorRequired)),
    streetNumber: dedicatedStreetNumber
      ? y.string().required(t(msg.streetNumberRequired))
      : y.string(),
    address: y.string().required(t(msg.addressRequired)),
    postalCode: zipcodesAllowed?.length
      ? y
          .string()
          .required(t(msg.postalCodeRequired))
          .oneOf(zipcodesAllowed, t(msg.postalCodeNotAllowed))
      : y.string().required(t(msg.postalCodeRequired)),
    city: citiesAllowed.length
      ? y
          .string()
          .required(t(msg.cityRequired))
          .test("cityCheck", t(msg.cityNotAllowed), (item) =>
            citiesAllowed.includes(item?.toLocaleLowerCase() || "")
          )
      : y.string().required(t(msg.cityRequired)),
  });
}

export type LocationControlFormValues = {
  streetNumber: string;
  address: string;
  city: string;
  lat: number | null;
  lon: number | null;
  postalCode: string;
  sector: { label: string; value: string };
  zone: { label: string; value: string };
};

export const emptyLocationControlFormValues: LocationControlFormValues = {
  streetNumber: "",
  address: "",
  city: "",
  lat: null,
  lon: null,
  postalCode: "",
  sector: {
    label: "",
    value: "",
  },
  zone: {
    label: "",
    value: "",
  },
};

type LocationControlFormErrors = {
  streetNumber: string[];
  address: string[];
  city: string[];
  postalCode: string[];
  sector: string[];
  zone: string[];
};

const emptyErrors: LocationControlFormErrors = {
  streetNumber: [],
  address: [],
  city: [],
  postalCode: [],
  sector: [],
  zone: [],
};

let initialized = false;
export function setInitialized(value: boolean): void {
  initialized = value;
}

export type LocationControlProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  onFormDataChange?: (formData: LocationControlFormValues) => void;
  values?: Partial<LocationControlFormValues>;
  onMapMatchingProcess2?: (
    licensePlate: string,
    params?: {
      accuracy?: number;
      latitude?: number;
      longitude?: number;
      scandate?: string;
      requestSrc?: string;
    }
  ) => void;
  ticket?: Ticket;
  lapiControl?: LapiControl;
  triggerLocationControlFormValidation?: boolean;
};

function LocationControl(props: LocationControlProps) {
  const {
    className,
    onFormDataChange = noop,
    onMapMatchingProcess2 = noop,
    values,
    ticket,
    lapiControl,
    triggerLocationControlFormValidation = false,
    ...other
  } = props;
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);
  const { cityConfig, sectors, zones } = useCityConfig();
  const [formData, setFormData] = React.useState<LocationControlFormValues>(
    emptyLocationControlFormValues
  );
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  const [errors, setErrors] =
    React.useState<LocationControlFormErrors>(emptyErrors);

  const validateField = React.useCallback(
    (
      name: keyof LocationControlFormValues,
      value: LocationControlFormValues
    ) => {
      getLocationControlFormSchema(
        yup,
        t,
        cityConfig?.review.dedicatedStreetNumber,
        cityConfig?.review.zipcodesAllowed || [],
        cityConfig?.review.citiesAllowed || []
      )
        .validateAt(name, value)
        .then((_validValue) => {
          setErrors((prev) => ({ ...prev, [name]: [] }));
        })
        .catch((error) => {
          setErrors((prev) => ({ ...prev, [name]: error.errors }));
        });
    },
    [
      t,
      cityConfig?.review.dedicatedStreetNumber,
      cityConfig?.review.zipcodesAllowed,
      cityConfig?.review.citiesAllowed,
    ]
  );

  const validateForm = React.useCallback(
    (value: LocationControlFormValues) => {
      validateField("streetNumber", value);
      validateField("address", value);
      validateField("city", value);
      validateField("postalCode", value);
      validateField("sector", value);
      validateField("zone", value);
    },
    [validateField]
  );

  // Basically the onChange event.
  React.useEffect(() => {
    onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  // Enable parent to trigger validation.
  React.useEffect(() => {
    if (!triggerLocationControlFormValidation) return;

    validateForm(formData);
  }, [formData, triggerLocationControlFormValidation, validateForm]);

  // Workflow of the gracePeriod, to move to another more suited place.
  React.useEffect(() => {
    if (
      lapiControl === undefined ||
      ticket === undefined ||
      zones === undefined
    ) {
      return;
    }

    const currentZone = zones.find((z) => z.code === formData.zone.value);
    if (currentZone === undefined) return;

    if (isTicketInGracePeriod(ticket, currentZone, lapiControl)) {
      const timeoutId = setTimeout(
        () => setNotification({ open: false }),
        3000
      );

      setNotification({
        open: true,
        message: t(msg.gracePeriodNotification),
        type: "info",
        onCloseButtonClick: (_event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          setNotification({ open: false });
        },
      });
    }
  }, [formData.zone.value, lapiControl, t, ticket, zones]);

  // When values changes, update the form accordingly.
  React.useEffect(() => {
    if (values === undefined || values.lat === null || values.lon === null) {
      setFormData({
        ...emptyLocationControlFormValues,
        sector: { ...emptyLocationControlFormValues.sector },
        zone: { ...emptyLocationControlFormValues.zone },
      });
      setErrors(emptyErrors);
      return;
    }
    const formData = {
      ...emptyLocationControlFormValues,
      ...values,
      sector: { ...emptyLocationControlFormValues.sector, ...values?.sector },
      zone: { ...emptyLocationControlFormValues.zone, ...values?.zone },
    };

    setFormData(formData);
    setErrors(emptyErrors);
    // reinit
    if (initialized) {
      newControleFeature([formData.lon, formData.lat]);
    }
  }, [values]);

  // Init the map.
  React.useEffect(() => {
    if (
      !cityConfig ||
      !sectors ||
      !zones ||
      formData.lat === null ||
      formData.lon === null
    )
      return;

    const map = document.getElementById("map");
    if (map) {
      map.innerHTML = "";
    }

    init({
      coord: [formData.lon, formData.lat],
      geocodingApiUrl: cityConfig?.geocodingApiUrl,
      secteurs: sectors,
      zones: zones,
      zoom: cityConfig?.review?.gis?.zoom,
      center: cityConfig?.review?.gis?.center,
      maxZoom: cityConfig?.review?.gis?.maxZoom,
      maxSatelliteZoom: cityConfig?.review?.gis?.satellite?.maxZoom,
      layers: cityConfig?.review?.gis?.layers,
      projection: cityConfig?.review?.gis?.projection,
      dedicatedStreetNumber: cityConfig?.review.dedicatedStreetNumber,
      onAddressChange: (
        value: Pick<
          LocationControlFormValues,
          "streetNumber" | "address" | "city" | "postalCode"
        >
      ) => {
        const fullAddress = value;
        setFormData((prev) => {
          const next = {
            ...prev,
            streetNumber:
              fullAddress.streetNumber ||
              emptyLocationControlFormValues.streetNumber,
            address:
              fullAddress.address || emptyLocationControlFormValues.address,
            city: fullAddress.city || emptyLocationControlFormValues.city,
            postalCode:
              fullAddress.postalCode ||
              emptyLocationControlFormValues.postalCode,
          };
          validateField("streetNumber", next);
          validateField("address", next);
          validateField("city", next);
          validateField("postalCode", next);
          return next;
        });
      },
      onCoordsChange: (value: [number, number]) => {
        const coordinates = value;
        setFormData((prev) => ({
          ...prev,
          lat: coordinates[1],
          lon: coordinates[0],
        }));
      },
      onMapMatchingProcess2: () => {
        onMapMatchingProcess2(globalState.licensePlate, {
          scandate: globalState.scanDate,
          latitude: globalState.lat ?? undefined,
          longitude: globalState.lon ?? undefined,
          requestSrc: cityConfig?.review.ticketsRequestSource
            ? "ImageReview"
            : undefined,
        });
      },
      onSectorChange: (value: Sector | "null") => {
        const sector = value;

        if (sector === "null") {
          setFormData((prev) => {
            const next = {
              ...prev,
              sector: { ...emptyLocationControlFormValues.sector },
            };
            validateField("sector", next);
            return next;
          });
          return;
        }

        setFormData((prev) => {
          const next = {
            ...prev,
            sector: { label: sector.name, value: sector.code },
          };
          validateField("sector", next);
          return next;
        });
      },
      onZoneChange: (value: Zone | "null") => {
        const zone = value;

        if (zone === "null") {
          setFormData((prev) => {
            const next = {
              ...prev,
              zone: { ...emptyLocationControlFormValues.zone },
            };
            validateField("zone", next);
            return next;
          });
          return;
        }

        setFormData((prev) => {
          const next = {
            ...prev,
            zone: { label: zone.name, value: zone.code },
          };
          validateField("zone", next);
          return next;
        });
      },
    });
    initControleFeature();
    setInitialized(true);
  }, [
    cityConfig,
    formData.lat,
    formData.lon,
    onMapMatchingProcess2,
    sectors,
    validateField,
    zones,
  ]);

  const handleStreetNumberChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const streetNumber = event.target.value;
      setFormData((prev) => {
        const next = { ...prev, streetNumber };
        validateField("streetNumber", next);
        return next;
      });
    },
    [validateField]
  );

  const handleAddressChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const address = event.target.value;
      setFormData((prev) => {
        const next = { ...prev, address };
        validateField("address", next);
        return next;
      });
    },
    [validateField]
  );

  const handleCityChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const city = event.target.value;
      setFormData((prev) => {
        const next = { ...prev, city };
        validateField("city", next);
        return next;
      });
    },
    [validateField]
  );

  const handlePostalCodeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const postalCode = event.target.value;
      setFormData((prev) => {
        const next = { ...prev, postalCode };
        validateField("postalCode", next);
        return next;
      });
    },
    [validateField]
  );

  const handleSectorChange = React.useCallback(
    (
      _event: React.ChangeEvent<{}>,
      value: LocationControlFormValues["sector"] | null
    ) => {
      const sector = value;

      if (sector === null) {
        setFormData((prev) => {
          const next = {
            ...prev,
            sector: { ...emptyLocationControlFormValues.sector },
          };
          validateField("sector", next);
          return next;
        });
        updateSecteur();
        return;
      }

      setFormData((prev) => {
        const next = { ...prev, sector: { ...sector } };
        validateField("sector", next);
        return next;
      });
      updateSecteur(sector.value);
    },
    [validateField]
  );

  const getSectorsOptions = React.useCallback(
    (
      sectors: Sector[] | undefined
    ): LocationControlFormValues["sector"][] | undefined => {
      if (sectors === undefined) return undefined;

      return sectors.map((sector) => ({
        label: sector.name,
        value: sector.code,
      }));
    },
    []
  );

  const getSectorAutocompleteOptionLabel = React.useCallback(
    (option: LocationControlFormValues["sector"]): string => option.label,
    []
  );

  const getSectorAutocompleteOptionSelected = React.useCallback(
    (
      option: LocationControlFormValues["sector"],
      value: LocationControlFormValues["sector"]
    ): boolean => option.value === value.value,
    []
  );

  const sectorAutocompleteFilterOptions = React.useMemo(
    () =>
      createFilterOptions<LocationControlFormValues["sector"]>({
        matchFrom: "start",
        stringify: (option) => option.label,
      }),
    []
  );

  const handleZoneChange = React.useCallback(
    (
      _event: React.ChangeEvent<{}>,
      value: LocationControlFormValues["zone"] | null
    ) => {
      const zone = value;

      if (zone === null) {
        setFormData((prev) => {
          const next = {
            ...prev,
            zone: { ...emptyLocationControlFormValues.zone },
          };
          validateField("zone", next);
          return next;
        });
        updateSecteur();
        return;
      }

      setFormData((prev) => {
        const next = { ...prev, zone: { ...zone } };
        validateField("zone", next);
        return next;
      });
      updateSecteur(zone.value);
    },
    [validateField]
  );

  const getZonesOptions = React.useCallback(
    (
      zones: Zone[] | undefined
    ): LocationControlFormValues["zone"][] | undefined => {
      if (zones === undefined) return undefined;

      return zones.map((zone) => ({
        label: zone.name,
        value: zone.code,
      }));
    },
    []
  );

  const getZoneAutocompleteOptionLabel = React.useCallback(
    (option: LocationControlFormValues["zone"]): string => option.label,
    []
  );

  const getZoneAutocompleteOptionSelected = React.useCallback(
    (
      option: LocationControlFormValues["zone"],
      value: LocationControlFormValues["zone"]
    ): boolean => option.value === value.value,
    []
  );

  const zoneAutocompleteFilterOptions = React.useMemo(
    () =>
      createFilterOptions<LocationControlFormValues["zone"]>({
        matchFrom: "start",
        stringify: (option) => option.label,
      }),
    []
  );

  const handleOnKeyUp = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.keyCode === 13) {
        const address = cityConfig?.review.dedicatedStreetNumber
          ? formData.streetNumber + formData.address
          : formData.address;

        addressUpdated(address, formData.city, formData.postalCode);
      }
    },
    [
      cityConfig?.review.dedicatedStreetNumber,
      formData.streetNumber,
      formData.address,
      formData.city,
      formData.postalCode,
    ]
  );

  const handleRestoreButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      history();
    },
    []
  );

  return (
    <>
      <div className={rootClasses} {...other}>
        <div className={classes.header}>
          <Typography variant="h6" noWrap>
            <T {...msg.title} />
          </Typography>
          <Tooltip title={t(msg.restoreButtonLabel)}>
            <span>
              <IconButton
                className={classes.resetButton}
                aria-label={t(msg.restoreButtonLabel)}
                color="inherit"
                onClick={handleRestoreButtonClick}
              >
                <RestoreIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <div className={classes.body}>
          <div className={classes.form}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete<LocationControlFormValues["zone"]>
                  TextFieldProps={{
                    name: "zone",
                    label: t(msg.zoneLabel),
                    required: true,
                    error: errors.zone.length > 0,
                    helperText: errors.zone[0],
                    fullWidth: true,
                  }}
                  value={formData.zone}
                  onChange={handleZoneChange}
                  options={getZonesOptions(zones)}
                  getOptionLabel={getZoneAutocompleteOptionLabel}
                  getOptionSelected={getZoneAutocompleteOptionSelected}
                  filterOptions={zoneAutocompleteFilterOptions}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete<LocationControlFormValues["sector"]>
                  TextFieldProps={{
                    name: "sector",
                    label: t(msg.sectorLabel),
                    required: true,
                    error: errors.sector.length > 0,
                    helperText: errors.sector[0],
                    fullWidth: true,
                  }}
                  value={formData.sector}
                  onChange={handleSectorChange}
                  options={getSectorsOptions(sectors)}
                  getOptionLabel={getSectorAutocompleteOptionLabel}
                  getOptionSelected={getSectorAutocompleteOptionSelected}
                  filterOptions={sectorAutocompleteFilterOptions}
                />
              </Grid>
              {cityConfig?.review.dedicatedStreetNumber && (
                <Grid item xs={12} md={3}>
                  <TextField
                    name="streetNumber"
                    label={t(msg.streetNumberLabel)}
                    required
                    error={errors.streetNumber.length > 0}
                    helperText={errors.streetNumber[0]}
                    fullWidth
                    inputProps={{ onKeyUp: handleOnKeyUp }}
                    value={formData.streetNumber}
                    onChange={handleStreetNumberChange}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={cityConfig?.review.dedicatedStreetNumber ? 9 : 12}
              >
                <TextField
                  name="address"
                  label={t(msg.addressLabel)}
                  required
                  error={errors.address.length > 0}
                  helperText={errors.address[0]}
                  fullWidth
                  inputProps={{ onKeyUp: handleOnKeyUp }}
                  value={formData.address}
                  onChange={handleAddressChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  name="postalCode"
                  label={t(msg.postalCodeLabel)}
                  required
                  error={errors.postalCode.length > 0}
                  helperText={errors.postalCode[0]}
                  fullWidth
                  inputProps={{ onKeyUp: handleOnKeyUp }}
                  value={formData.postalCode}
                  onChange={handlePostalCodeChange}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  name="city"
                  label={t(msg.cityLabel)}
                  required
                  error={errors.city.length > 0}
                  helperText={errors.city[0]}
                  fullWidth
                  inputProps={{ onKeyUp: handleOnKeyUp }}
                  value={formData.city}
                  onChange={handleCityChange}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
      <FullscreenNotification {...notification} />
    </>
  );
}

export default LocationControl;
